import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { toProjects } from 'ducks/router'
import { useTable } from 'hooks'
import { jobs } from 'orm'
import { getColumns, getFiltersConfigs } from './model'
import { getFilterQuery } from '../../../helpers'
import type { TableQuery } from 'hooks/useTable'

const useJobsTableMutations = () => {
  const dispatch = useDispatch()

  const handleFetchJobs = React.useCallback(
    ({ limit, offset, ordering, search, active, department }: TableQuery) => {
      dispatch(
        jobs.fetchStart({
          limit,
          offset,
          ordering,
          search,
          active,
          department,
        }),
      )
    },
    [dispatch],
  )

  return {
    handleFetchJobs,
  }
}

const useJobsTableData = () => {
  const data = useSelector(jobs.getModel)

  const { count, items = [], meta, order = [] } = data ?? {}

  return {
    state: { count, items, meta, order },
  }
}

export const useJobsTable = state => {
  const { mapSortingPropToQueryParam, search, filter } = state

  const dispatch = useDispatch()

  const columns = getColumns()

  const { handleFetchJobs } = useJobsTableMutations()

  const {
    state: { count, items, meta, order },
  } = useJobsTableData()

  const handleRefetch = React.useCallback(
    ({ limit, offset, ordering, search, active, department }: TableQuery) => {
      handleFetchJobs({ limit, offset, ordering, search, active, department })
    },
    [handleFetchJobs],
  )

  const handleQueriesUpdate = React.useCallback(
    ({ limit, offset, ordering, search, active, department }: TableQuery) => {
      dispatch(
        toProjects(null, null, {
          limit,
          offset,
          ordering,
          search,
          active,
          department,
        }),
      )
    },
    [dispatch],
  )

  const handleSelection = React.useCallback(
    payload => {
      dispatch(jobs.setSelection(payload))
    },
    [dispatch],
  )

  const handleStep = React.useCallback(
    id => (tab, queries) => {
      dispatch(toProjects(id, tab, queries))
    },
    [dispatch],
  )

  const filterQuery = React.useMemo(
    () => getFilterQuery(filter, getFiltersConfigs()),
    [filter],
  )

  const {
    state: {
      pagination: { currentPage, pageCount, perPage },
      table: {
        headers,
        model,
        rowSelection,
        isAllRowsSelected,
        isFetching,
        isSomeRowsSelected,
      },
    },
    handlers: {
      pagination: { handlePage, handlePageSize },
      table: { toggleAllRowsSelectedHandler },
    },
  } = useTable({
    columns,
    data: order.map(key => items[key]),
    mapSortingPropToQueryParam,
    pageCount: count,
    rowSelection: meta?.selected,
    search,
    filterQuery,
    fetchAction: handleRefetch,
    updateQueriesAction: handleQueriesUpdate,
    onRowSelectionChange: handleSelection,
  })

  return {
    state: {
      pagination: { currentPage, pageCount, perPage },
      table: {
        headers,
        model,
        rowSelection,
        isAllRowsSelected,
        isFetching,
        isSomeRowsSelected,
      },
    },
    handlers: {
      handleStep,
      pagination: { handlePage, handlePageSize },
      table: { toggleAllRowsSelectedHandler },
    },
  }
}
